import Hls, { FragmentLoaderContext, HlsConfig, Loader, LoaderCallbacks, LoaderConfiguration, LoaderContext, LoaderStats, PlaylistLoaderContext } from "hls.js";
import { FC, useEffect, useState } from "react";
import * as uuid from "uuid";
import { VideoPlayer } from "./VideoPlayer";

const sendMessageWhenConnected = (socket: WebSocket, message: string | ArrayBufferLike | ArrayBufferView | Blob) => {
  return new Promise<void>((resolve, reject) => {
    if (socket.readyState === WebSocket.OPEN) {
      // WebSocketが既に接続されている場合はそのままメッセージを送信
      socket.send(message);
      resolve();
    } else {
      // WebSocketが接続されていない場合は接続イベントを待機
      socket.addEventListener('open', () => {
        socket.send(message);
        resolve();
      });
      // エラーハンドリング
      socket.addEventListener('error', (error) => {
        reject(error);
      });
    }
  });
}

const getFilenameFromURL = (url: string) => {
  const filePath = new URL(url).pathname;
  let filename = filePath.substring(filePath.lastIndexOf('/') + 1);
  return filename.split('?')[0];
}

const blobToArrayBuffer = (blob: Blob) => {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
}

const splitArrayBufferAtNull = (arrayBuffer: ArrayBuffer): [ArrayBuffer, ArrayBuffer|null] => {
  const uint8Array = new Uint8Array(arrayBuffer);

  // ヌル文字が最初に出現するインデックスを検索
  const nullIndex = uint8Array.indexOf(0);

  if (nullIndex === -1) {
    // ヌル文字が見つからない場合は、元のArrayBufferを返す
    return [arrayBuffer, null];
  }

  // ヌル文字の前後でArrayBufferを分割
  const firstPart = arrayBuffer.slice(0, nullIndex);
  const secondPart = arrayBuffer.slice(nullIndex + 1);

  return [firstPart, secondPart];
}

function arrayBufferToString(arrayBuffer: ArrayBufferLike, encoding = "utf-8") {
  const decoder = new TextDecoder(encoding);
  const utf8Array = new Uint8Array(arrayBuffer);
  return decoder.decode(utf8Array);
}

const createStats = (): LoaderStats => {
  return {
    aborted: false,
    loaded: 0,
    retry: 0,
    total: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { start: performance.now(), first: performance.now(), end: performance.now() },
    parsing: { start: 0, end: 0 },
    buffering: { start: 0, first: 0, end: 0 }
  };
}

const fastLoad = (context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>, object: Uint8Array) => {
  setTimeout(() => {
    const objectBuff = object;
    const stats = createStats();
    const data = context.responseType == "text" ? arrayBufferToString(objectBuff) : objectBuff;
    const len = typeof data == "string" ? data.length : data.byteLength;
    stats.loaded = stats.total = len;
    const response = {
      url: context.url,
      data: data,
      code: 200,
    };
    callbacks.onSuccess(response, stats, context, null);
  }, 1);
}

class HTTPLoader {
  private _fastLoadObjects: { [key: string]: Uint8Array };

  constructor(fastLoadObjects: { [key: string]: Uint8Array }) {
    this._fastLoadObjects = fastLoadObjects;
  }

  get loader(): new (config: HlsConfig) => Loader<LoaderContext> {
    const httpLoader = this;
    return class extends Hls.DefaultConfig.loader {
      constructor(config: HlsConfig) {
        super(config);
      }

      load(context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>) {
        const key = getFilenameFromURL(context.url);
        if (httpLoader._fastLoadObjects[key]) {
          fastLoad(context, config, callbacks, httpLoader._fastLoadObjects[key]);
        } else {
          if ((window as any).DEBUG) {
            context.url += `?t=${uuid.v4()}`;
          }
          super.load(context, config, callbacks);
        }
      };
    }
  }
}

// src/utils/xhr-loader.tsを参考にする
class WebSocketLoader {
  private _socket: WebSocket
  private _objectLoaders: { key: string, loader: Loader<LoaderContext> & { handleSuccess(objectBuff: ArrayBuffer): void, handleCancel(): void, loadFromHttp(): void } }[];
  private _fastLoadObjects: { [key: string]: Uint8Array }

  constructor(src: string, fastLoadObjects: { [key: string]: Uint8Array }) {
    this._objectLoaders = [];
    this._fastLoadObjects = fastLoadObjects;
    this._socket = new WebSocket(src);
    this._socket.onmessage = async (event: MessageEvent<Blob>) => {
      const buff = await blobToArrayBuffer(event.data);
      const [keyBuff, objectBuff] = splitArrayBufferAtNull(buff);
      if (objectBuff) {
        const key = arrayBufferToString(keyBuff);
        console.log(`Fetched: ${key}`);
        // 配信したぶんは取り除く。以前は slice を呼んでいたが、slice は新しい
        // 配列を返すだけで元を変えないため、待ち行列が一度も縮まなかった。
        // 同じセグメントを再度要求すると、過去の（破棄済みの）ローダーにも
        // 配信しようとする。
        const done = this._objectLoaders.filter(entry => entry.key == key);
        this._objectLoaders = this._objectLoaders.filter(entry => entry.key != key);
        done.forEach(entry => entry.loader.handleSuccess(objectBuff));
      }
    };
    this._socket.onclose = async (ev: CloseEvent) => {
      // 生成が終わると GPU 側がソケットを閉じる。取得中のフラグメントを
      // ここでエラーにすると、hls.js は 4xx を再試行しないので**そのまま
      // 止まる**（実測：最後に届いたセグメントの位置で readyState=2 のまま
      // 固まり、以降フラグメントの要求が出なくなる）。生成が終わっていれば
      // セグメントは CDN に上がっているので、そちらから取り直す。
      console.log(`on close (取得中 ${this._objectLoaders.length} 件)`);
      const pending = this._objectLoaders;
      this._objectLoaders = [];
      pending.forEach(({ loader }) => loader.loadFromHttp());
    };
  }

  close() {
    this._socket.close();
  };

  get loader(): new (config: HlsConfig) => Loader<LoaderContext> {
    const websocketLoader = this;
    return class {
      private _objectKey: string|null;
      private _callbacks: LoaderCallbacks<LoaderContext>|null;

      public context: LoaderContext|null;
      public stats: LoaderStats;

      constructor() {
        this._objectKey = null;
        this._callbacks = null;
        this.context = null;
        this.stats = createStats();
      }

      handleSuccess(objectBuff: ArrayBuffer) {
        if (!this.context || !this._callbacks) {
          return;
        }
        const data = this.context.responseType == "text" ? arrayBufferToString(objectBuff) : objectBuff;
        const len = typeof data == "string" ? data.length : data.byteLength;

        const response = { url: this.context.url, data, code: 200 };

        this.stats.loading.end = performance.now();
        this.stats.loaded = this.stats.total = len;
        this.stats.bwEstimate = (this.stats.total * 8000) / (this.stats.loading.end - this.stats.loading.first);

        this._callbacks.onSuccess(response, this.stats, this.context, null);
      }

      handleCancel() {
        this._callbacks?.onError(
          { code: 410, text: "Websocket closed" },
          this.context as LoaderContext,
          null,
          this.stats
        );
      }

      /**
       * WebSocket が閉じたあと、同じものを CDN から取り直す。
       *
       * ソケットが閉じるのは生成が終わったときなので、セグメントは R2 に
       * 上がっている。ここでエラーを返すと hls.js が再試行せずに止まる。
       */
      async loadFromHttp() {
        if (!this.context || !this._callbacks) {
          return;
        }
        try {
          const response = await fetch(this.context.url);
          if (!response.ok) {
            throw new Error(String(response.status));
          }
          this.handleSuccess(await response.arrayBuffer());
        } catch (error) {
          console.log(`CDN からも取れなかった: ${this._objectKey} ${error}`);
          this.handleCancel();
        }
      }

      destroy() {
        this._objectKey = null;
        this._callbacks = null;
        this.context = null;
        // @ts-ignore
        this.stats = null;
      }

      async abort() {
        await sendMessageWhenConnected(websocketLoader._socket, `cancel:${this._objectKey}`);
        if (this._callbacks?.onAbort) {
          this._callbacks.onAbort(
            this.stats,
            this.context as LoaderContext,
            null
          );
        }
      }

      async load(context: LoaderContext, config: LoaderConfiguration, callbacks: LoaderCallbacks<LoaderContext>) {
        this.context = context;
        this._callbacks = callbacks;

        const key = getFilenameFromURL(context.url);
        this._objectKey = key;

        if (websocketLoader._fastLoadObjects[key]) {
          fastLoad(context, config, callbacks, websocketLoader._fastLoadObjects[key]);
        } else {
          console.log(`Fetching: ${key}`);
          websocketLoader._objectLoaders.push({ key, loader: this });
          await sendMessageWhenConnected(websocketLoader._socket, `request:${key}`);
        }
      }
    }
  }
};

const createLoaderWapper = (getLoader: () => new (config: HlsConfig) => Loader<LoaderContext>): new (config: HlsConfig) => Loader<LoaderContext> => {
  return function (this: Loader<LoaderContext>, config: HlsConfig) {
    const loaderType = getLoader();
    const loader = new loaderType(config);
    Object.assign(this, loader);
    (this as any).__proto__ = loaderType.prototype;
  } as any;
}

export const WebSocketPlayer: FC<{ wsSrc: string, httpSrc: string, getUseHttp: () => boolean, license: string, fastLoadObjects: { [key: string]: Uint8Array } }> = (props) => {
  const [webSocketLoader, setWebSocketLoader] = useState<WebSocketLoader|null>(null);

  useEffect(() => {
    if (props.getUseHttp()) {
      setWebSocketLoader(null);
      return;
    }
    const newLoader = new WebSocketLoader(props.wsSrc, props.fastLoadObjects);
    setWebSocketLoader(newLoader);
    return () => {
      newLoader.close();
    };
  }, [props.wsSrc, props.fastLoadObjects, props.getUseHttp()]);

  // 以前はここで、生成中のシーク位置を 2 秒（セグメント長）の倍数に切り捨てて
  // いた。**これが「シークすると再生が止まる」の原因だったので外した。**
  //
  // セグメントは 2N 秒ちょうどから始まらない。1 本ずつ discontinuity を打って
  // いる都合で境界には 0.1〜0.5 秒の穴があり、切り捨てた先にはデータが無い。
  // 実際、8 分の動画で 368.8 へシークすると 368.0 に落とされ、実データは
  // 368.4 からなので readyState=1 のまま止まった。
  //
  // さらにこの丸めは seeking イベントで走るため、hls.js が穴を飛び越えようと
  // して currentTime を進めるたびに引き戻していた。370.38 と 370.00 の往復で
  // 固まり、45 秒待っても 1 フレームも進まない状態になる。
  //
  // hls.js は指定位置を含むセグメントを自分で選んで取りに行くので、こちらで
  // 位置を触る必要はない。

  const loader = createLoaderWapper(() => {
    return props.getUseHttp() 
      ? (new HTTPLoader(props.fastLoadObjects)).loader
      : (webSocketLoader?.loader || new HTTPLoader(props.fastLoadObjects).loader);
  });

  const hlsConfig: Partial<HlsConfig> = {
    pLoader: loader as new (config: HlsConfig) => Loader<PlaylistLoaderContext>,
    fLoader: loader as new (config: HlsConfig) => Loader<FragmentLoaderContext>,
    // fragLoadingTimeOut: 5000,
    maxMaxBufferLength: props.getUseHttp() ? 600 : 30, // デフォルトで600秒であるため、セグメントが2秒のため300回のリクエストが飛ぶのを防ぐ

    // セグメント境界ごとに 0.08〜0.1 秒の穴が空く。1 本ずつ discontinuity を
    // 打っている都合で、バッファは [0, 2] [2.08, 4.1] [4.18, 6.2] … と切れ目
    // だらけになる（15 分の動画で 220 区間）。既定の maxBufferHole は 0.1 秒で
    // 穴と同値なので、飛び越えられないことがある。穴はセグメント長（2 秒）より
    // 十分小さいので、まとめて飛ばしてよい。
    //
    // **ただしこれだけでは「再生が始まらない」不具合は直らない。** 別の原因が
    // 残っている（docs/frontend/components.md 参照）。
    maxBufferHole: 0.5,
  };

  return <VideoPlayer key={props.httpSrc} src={props.httpSrc} license={props.license} hlsConfig={hlsConfig}/>;
};

export default WebSocketPlayer;
